import {
  Alert,
  Card,
  Col,
  DatePicker,
  Divider,
  Flex,
  Form,
  Input,
  Row
} from 'antd'
import { Fragment } from 'react'

import { TriState, HivAnamnesisFormSection } from 'src/api/requests'
import { CountrySelect, Text } from 'src/components'
import { dayMonthYearFormatWithDot, disabledFutureDate } from 'src/libs'
import { useLocales } from 'src/locales'

import { useHivAnamnesisClassifiers } from '../../hooks/useHivAnamnesisClassifiers'
import { isChildOn } from '../../utils'
import { HivClassifierSelect } from '../fields/HivClassifierSelect'
import { TriStateItem } from '../fields/TriStateItem'
import { HivFormItem } from '../HivFormItem'
import { SystemRefreshButton } from '../SystemRefreshButton'

const ADULT_QUESTIONS = [
  ['21', 'sexWithMan'],
  ['22', 'sexWithWoman'],
  ['23', 'injectingDrugUse'],
  ['24', 'nonInjectingDrugUse']
] as const

const HETEROSEXUAL_QUESTIONS = [
  ['25.1', 'withHivInfectedPerson'],
  ['25.2', 'withInjectingDrugUser'],
  ['25.3', 'withBisexualMan'],
  ['25.4', 'withSexWorker'],
  ['25.5', 'withHaemophiliac'],
  ['25.6', 'withBloodRecipient'],
  ['25.7', 'withMultiplePartners'],
  ['25.8', 'withCasualPartners'],
  ['25.9', 'withNosocomiallyInfectedPerson'],
  ['25.10', 'withPerinatallyInfectedPerson'],
  ['25.11', 'withPersonAbroad']
] as const

const MOTHER_QUESTIONS = [
  ['30.1', 'motherArvBeforePregnancy'],
  ['30.2', 'motherArvDuringPregnancy'],
  ['30.3', 'motherArvAtBirth'],
  ['31.1', 'breastfed'],
  ['31.2', 'prophylacticArvAfterBirth'],
  ['32.1', 'motherInjectingDrugUse'],
  ['32.2', 'motherPerinatallyInfected']
] as const

const MOTHER_HETEROSEXUAL_QUESTIONS = [
  ['33.1', 'withHivInfectedPerson'],
  ['33.2', 'withInjectingDrugUser'],
  ['33.3', 'withBisexualMan'],
  ['33.4', 'withHaemophiliac'],
  ['33.5', 'withBloodRecipient'],
  ['33.6', 'withMultiplePartners'],
  ['33.7', 'withCasualPartners'],
  ['33.8', 'withPerinatallyInfectedPerson']
] as const

const MOTHER_EXPOSURE_QUESTIONS = [
  ['34.1', 'motherTransfusion'],
  ['34.2', 'motherCoagulationFactors'],
  ['34.3', 'motherTransplantOrInsemination'],
  ['35.1', 'childSexWithMan'],
  ['35.2', 'childSexWithWoman'],
  ['35.3', 'childInjectingDrugUse'],
  ['35.4', 'childNonInjectingDrugUse']
] as const

const EXPOSURES = [
  ['36', 'transfusion'],
  ['37', 'coagulationFactors'],
  ['38', 'transplantOrInsemination']
] as const

/** The applicable age group, from the birth date (7) on the collection date (4.1). */
export const useIsChild = () => {
  const form = Form.useFormInstance()
  const birthDate = Form.useWatch(['form', 'patient', 'birthDate'], form)
  const collectedAt = Form.useWatch(
    ['form', 'institutions', 'collectedAt'],
    form
  )

  return isChildOn(birthDate, collectedAt)
}

/** The numbered heading of a D.2 group (30, 31, 32, 34, 35), shown before its first question. */
const GroupHeading = ({
  number,
  previous
}: {
  number: string
  previous?: string
}) => {
  const { t } = useLocales()
  const group = number.split('.')[0]
  if (previous?.split('.')[0] === group) return null

  return (
    <Col span={24}>
      <Text>{`${group}. ${t(`hiv_anamnesis.transmission.group_${group}`)}`}</Text>
    </Col>
  )
}

const DatedExposure = ({
  field,
  number
}: {
  field: (typeof EXPOSURES)[number][1]
  number: string
}) => {
  const { t } = useLocales()
  const form = Form.useFormInstance()
  const path = (part: string) => ['form', 'transmissionOther', field, part]
  const answer = Form.useWatch(path('answer'), form)

  return (
    <Row gutter={[24, 24]}>
      <TriStateItem
        name={path('answer')}
        number={number}
        label={t(`hiv_anamnesis.transmission.q.${field}`)}
      />

      {answer === TriState.YES && (
        <>
          <Col xs={24} lg={6}>
            <HivFormItem
              name={path('firstDate')}
              number={`${number}.1`}
              label={t('hiv_anamnesis.transmission.first_date')}
            >
              <DatePicker
                className="full-width"
                format={dayMonthYearFormatWithDot}
                disabledDate={disabledFutureDate}
              />
            </HivFormItem>
          </Col>

          <Col xs={24} lg={6}>
            <HivFormItem
              name={path('lastDate')}
              number={`${number}.2`}
              label={t('hiv_anamnesis.transmission.last_date')}
              dependencies={[path('firstDate')]}
              rules={[
                ({ getFieldValue }) => ({
                  validator: (_, value) => {
                    const first = getFieldValue(path('firstDate'))

                    return value && first && value.isBefore(first, 'day')
                      ? Promise.reject(
                          t('hiv_anamnesis.transmission.last_before_first')
                        )
                      : Promise.resolve()
                  }
                })
              ]}
            >
              <DatePicker
                className="full-width"
                format={dayMonthYearFormatWithDot}
                disabledDate={disabledFutureDate}
              />
            </HivFormItem>
          </Col>
        </>
      )}
    </Row>
  )
}

/** D.1 (21–25) for patients aged 15 and over. Optional since 2026-10-15, also for a heterosexual route (43). */
const AdultSection = () => {
  const { t } = useLocales()
  const form = Form.useFormInstance()
  const path = (...parts: string[]) => ['form', 'transmissionAdult', ...parts]
  const withPersonAbroad = Form.useWatch(
    path('heterosexual', 'withPersonAbroad'),
    form
  )

  return (
    <Flex vertical gap={16}>
      <Text size="xl">{t('hiv_anamnesis.transmission.adult_title')}</Text>

      <Row gutter={[24, 24]}>
        {ADULT_QUESTIONS.map(([number, field]) => (
          <TriStateItem
            key={field}
            name={path(field)}
            number={number}
            label={t(`hiv_anamnesis.transmission.q.${field}`)}
          />
        ))}
      </Row>

      <Text>{`25. ${t('hiv_anamnesis.transmission.heterosexual_title')}`}</Text>

      <Row gutter={[24, 24]}>
        {HETEROSEXUAL_QUESTIONS.map(([number, field]) => (
          <TriStateItem
            key={field}
            name={path('heterosexual', field)}
            number={number}
            label={t(`hiv_anamnesis.transmission.heterosexual.${field}`)}
          />
        ))}

        {withPersonAbroad === TriState.YES && (
          <Col xs={24} lg={12}>
            <HivFormItem
              name={path('heterosexual', 'abroadCountry')}
              number="25.11"
              label={t('hiv_anamnesis.transmission.abroad_country')}
            >
              <CountrySelect allowClear />
            </HivFormItem>
          </Col>
        )}
      </Row>
    </Flex>
  )
}

/** D.2 (26–35) for children under 15. */
const ChildSection = () => {
  const { t } = useLocales()
  const { motherStatusId } = useHivAnamnesisClassifiers()
  const path = (...parts: string[]) => ['form', 'transmissionChild', ...parts]

  return (
    <Flex vertical gap={16}>
      <Text size="xl">{t('hiv_anamnesis.transmission.child_title')}</Text>

      <Row gutter={[24, 24]}>
        <Col xs={24} lg={12}>
          <HivFormItem
            name={path('motherHivStatusId')}
            number="26"
            label={t('hiv_anamnesis.transmission.q.motherHivStatusId')}
          >
            <HivClassifierSelect classifierId={motherStatusId} />
          </HivFormItem>
        </Col>

        <TriStateItem
          name={path('motherTestedDuringPregnancy')}
          number="27"
          label={t('hiv_anamnesis.transmission.q.motherTestedDuringPregnancy')}
        />

        <Col xs={24} lg={12}>
          <HivFormItem
            name={path('motherDiagnosisDate')}
            number="28"
            label={t('hiv_anamnesis.transmission.q.motherDiagnosisDate')}
          >
            <DatePicker
              className="full-width"
              format={dayMonthYearFormatWithDot}
              disabledDate={disabledFutureDate}
            />
          </HivFormItem>
        </Col>

        <Col xs={24} lg={12}>
          <HivFormItem
            name={path('motherSimeCode')}
            number="29"
            label={t('hiv_anamnesis.transmission.q.motherSimeCode')}
          >
            <Input maxLength={100} />
          </HivFormItem>
        </Col>

        {MOTHER_QUESTIONS.map(([number, field], index) => (
          <Fragment key={field}>
            <GroupHeading
              number={number}
              previous={MOTHER_QUESTIONS[index - 1]?.[0]}
            />
            <TriStateItem
              name={path(field)}
              number={number}
              label={t(`hiv_anamnesis.transmission.q.${field}`)}
            />
          </Fragment>
        ))}
      </Row>

      <Text>{`33. ${t('hiv_anamnesis.transmission.mother_heterosexual_title')}`}</Text>
      <Row gutter={[24, 24]}>
        {MOTHER_HETEROSEXUAL_QUESTIONS.map(([number, field]) => (
          <TriStateItem
            key={field}
            name={path('motherHeterosexual', field)}
            number={number}
            label={t(`hiv_anamnesis.transmission.heterosexual.${field}`)}
          />
        ))}

        {MOTHER_EXPOSURE_QUESTIONS.map(([number, field], index) => (
          <Fragment key={field}>
            <GroupHeading
              number={number}
              previous={MOTHER_EXPOSURE_QUESTIONS[index - 1]?.[0]}
            />
            <TriStateItem
              name={path(field)}
              number={number}
              label={t(`hiv_anamnesis.transmission.q.${field}`)}
            />
          </Fragment>
        ))}
      </Row>
    </Flex>
  )
}

/** D.3 (36–43): blood, occupational exposure, other risks and the probable route (43, the only required field). */
const OtherSection = () => {
  const { t } = useLocales()
  const form = Form.useFormInstance()
  const { transmissionRouteId } = useHivAnamnesisClassifiers()
  const path = (...parts: string[]) => ['form', 'transmissionOther', ...parts]

  const occupationalExposure = Form.useWatch(path('occupationalExposure'), form)
  const otherRisks = Form.useWatch(path('otherRisks'), form)

  return (
    <Flex vertical gap={16}>
      <Text size="xl">{t('hiv_anamnesis.transmission.other_title')}</Text>

      <Flex vertical gap={24}>
        {EXPOSURES.map(([number, field]) => (
          <DatedExposure key={field} field={field} number={number} />
        ))}
      </Flex>

      <Row gutter={[24, 24]}>
        <TriStateItem
          name={path('occupationalExposure')}
          number="39"
          label={t('hiv_anamnesis.transmission.q.occupationalExposure')}
        />

        {occupationalExposure === TriState.YES && (
          <>
            <Col xs={24} lg={6}>
              <HivFormItem
                name={path('occupationalInstitution')}
                label={t('hiv_anamnesis.transmission.occupational_institution')}
              >
                <Input maxLength={250} />
              </HivFormItem>
            </Col>

            <Col xs={24} lg={6}>
              <HivFormItem
                name={path('occupation')}
                label={t('hiv_anamnesis.transmission.occupation')}
              >
                <Input maxLength={250} />
              </HivFormItem>
            </Col>
          </>
        )}
      </Row>

      <Row gutter={[24, 24]}>
        <TriStateItem
          name={path('otherRisks')}
          number="40"
          label={t('hiv_anamnesis.transmission.q.otherRisks')}
        />

        {otherRisks === TriState.YES && (
          <Col xs={24} lg={12}>
            <HivFormItem
              name={path('otherRisksDescription')}
              label={t('hiv_anamnesis.transmission.other_risks_description')}
            >
              <Input.TextArea rows={2} maxLength={1000} />
            </HivFormItem>
          </Col>
        )}
      </Row>

      <Row gutter={[24, 24]}>
        <TriStateItem
          name={path('possibleNosocomialRoute')}
          number="41"
          label={t('hiv_anamnesis.transmission.q.possibleNosocomialRoute')}
        />

        <TriStateItem
          name={path('cluster')}
          number="42"
          label={t('hiv_anamnesis.transmission.q.cluster')}
        />

        <Col xs={24} lg={12}>
          <HivFormItem
            name={path('probableRouteId')}
            number="43"
            label={t('hiv_anamnesis.transmission.q.probableRouteId')}
            rules={[{ required: true }]}
          >
            <HivClassifierSelect classifierId={transmissionRouteId} />
          </HivFormItem>
        </Col>
      </Row>
    </Flex>
  )
}

/**
 * D. Transmission routes (fields 21–43). Only the section of the patient's age group is shown:
 * D.1 from 15 years, D.2 under 15; the other is emptied when the form is saved.
 */
export const TransmissionCard = () => {
  const { t } = useLocales()
  const isChild = useIsChild()

  return (
    <Card
      title={t('hiv_anamnesis.transmission.title')}
      extra={
        <SystemRefreshButton
          sections={[
            HivAnamnesisFormSection.TRANSMISSION_ADULT,
            HivAnamnesisFormSection.TRANSMISSION_CHILD,
            HivAnamnesisFormSection.TRANSMISSION_OTHER
          ]}
        />
      }
    >
      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
        message={t(
          isChild
            ? 'hiv_anamnesis.transmission.child_applies'
            : 'hiv_anamnesis.transmission.adult_applies'
        )}
      />

      {isChild ? <ChildSection /> : <AdultSection />}

      <Divider />
      <OtherSection />
    </Card>
  )
}
